/**
 * ODBM About & Details — cross-section variant refresh for
 * sections/odbm-product-details-area.liquid.
 *
 * Whole-file ODBM module (basename `odbm-*`), loaded by the section only
 * when a `details` block is configured. Tested in
 * tests/odbm-product-details-area.test.js (`bun run test`).
 *
 * Why this exists: the Product Details table's variant-scoped rows (SKU,
 * barcode, trim size, page count) must follow the selected variant, but this
 * section lives OUTSIDE the main-product <product-info> element, so the
 * vendor's updateSourceFromDestination refresh (scoped to `this` inside
 * product-info.js) can never reach it.
 *
 * Mechanism: product-info.js dispatches a document-level "variant:changed"
 * CustomEvent (detail.variant) on every variant change. This element listens
 * for it, fetches its own section render for that variant via the Section
 * Rendering API (`{productUrl}?variant={id}&section_id={sectionId}`), and
 * swaps the `[id^="OdbmProductMeta-"]` wrapper's innerHTML — ONLY the table,
 * so open accordions and the rest of the section keep their state. In-flight
 * requests are aborted when the shopper switches again quickly.
 */
(() => {
  if (customElements.get("odbm-details-area")) return;

  class OdbmDetailsArea extends HTMLElement {
    connectedCallback() {
      // Don't cache children here: when the element upgrades during HTML
      // parsing, connectedCallback fires before its children exist. The
      // meta wrapper is queried lazily per event instead.
      this.boundOnVariantChanged = this.onVariantChanged.bind(this);
      document.addEventListener("variant:changed", this.boundOnVariantChanged);
    }

    disconnectedCallback() {
      document.removeEventListener(
        "variant:changed",
        this.boundOnVariantChanged
      );
      this.abortController?.abort();
    }

    onVariantChanged(event) {
      const variant = event.detail?.variant;
      if (!variant?.id) return;
      // No details block → nothing variant-scoped to refresh.
      if (!this.querySelector('[id^="OdbmProductMeta-"]')) return;
      this.refresh(variant.id);
    }

    refresh(variantId) {
      const sectionId = this.dataset.sectionId;
      const productUrl = this.dataset.productUrl;
      if (!sectionId || !productUrl) return;

      this.abortController?.abort();
      this.abortController = new AbortController();

      const url = `${productUrl}?variant=${variantId}&section_id=${sectionId}`;
      return fetch(url, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const source = html.querySelector('[id^="OdbmProductMeta-"]');
          const destination = this.querySelector('[id^="OdbmProductMeta-"]');
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
          }
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("[odbm-details-area]", error);
          }
        });
    }
  }

  customElements.define("odbm-details-area", OdbmDetailsArea);
})();
